import { PayloadAction, createAsyncThunk, createSlice, current } from '@reduxjs/toolkit'
import { FeedbackRequest, FeedbackResponse } from '@typings/api/feedbackRequest.types'
import { FeedbackEvent } from '@typings/feedbackEvent.types'
import { FeedbackState } from '@typings/store/feedbackState.types'

import api from '../../services/api'

const initialState: FeedbackState = {
  ongoingFeedbackEvent: null,
  completedFeedbackEvents: [],
  error: false,
  loading: false,
  feedbackSent: false,
  rating: null,
}

export const sendFeedback = createAsyncThunk('feedback/send', async (data: Partial<FeedbackRequest>, thunkAPI) => {
  try {
    const response = await api.post<FeedbackResponse>({
      url: '/feedback',
      data: { ...data, type: 'other' },
    })
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const feedbackSlice = createSlice({
  name: 'feedbackState',
  initialState,
  reducers: {
    triggerFeedbackEvent: (store, action: PayloadAction<FeedbackEvent>) => {
      const feedbackEvent = action.payload
      const storedFeedbackEvents = current(store.completedFeedbackEvents)

      if (!checkIfFeedbackHasBeenGiven(storedFeedbackEvents, feedbackEvent)) {
        store.ongoingFeedbackEvent = feedbackEvent
      }
    },
    concludeFeedbackEvent: (store, action: PayloadAction<FeedbackEvent>) => {
      const feedbackEvent = action.payload
      const storedFeedbackEvents = current(store.completedFeedbackEvents)

      if (!checkIfFeedbackHasBeenGiven(storedFeedbackEvents, feedbackEvent)) {
        const updatedCompletedFedbackEvents = [...storedFeedbackEvents, feedbackEvent]
        store.completedFeedbackEvents = updatedCompletedFedbackEvents
        localStorage.setItem('completed-feedback', JSON.stringify(updatedCompletedFedbackEvents))
      }

      store.ongoingFeedbackEvent = null
    },
    setError: (store, action: PayloadAction<boolean>) => {
      store.error = action.payload
    },
    setLoading: (store, action: PayloadAction<boolean>) => {
      store.loading = action.payload
    },
    setRating: (store, action: PayloadAction<number | null>) => {
      store.rating = action.payload
    },
    resetFeedbackState: (store) => {
      store.feedbackSent = false
      store.rating = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendFeedback.pending, (store) => {
        store.loading = true
        store.error = false
      })
      .addCase(sendFeedback.fulfilled, (store, action) => {
        store.feedbackSent = action.payload.status === 200
        store.loading = false
        store.error = false
      })
      .addCase(sendFeedback.rejected, (store) => {
        store.loading = false
        store.error = true
        store.feedbackSent = false
      })
  },
})

const checkIfFeedbackHasBeenGiven = (completedFeedback: FeedbackEvent[], feedbackEvent: FeedbackEvent) => {
  return completedFeedback.find(
    (completedFeedback) =>
      completedFeedback.feedbackType === feedbackEvent.feedbackType &&
      completedFeedback.raNumber === feedbackEvent.raNumber
  )
}

export const { triggerFeedbackEvent, concludeFeedbackEvent, setError, setRating, resetFeedbackState } =
  feedbackSlice.actions
export const feedbackStateReducer = feedbackSlice.reducer
